import { Component, computed, effect, input, signal } from '@angular/core';
import { ContentText, WireframeQuestion, WireframeReportResponse } from '@mira-avant/mtlibs';
import { AngularModule } from '../../../../../modules/angular.module';
import { ZorroModule } from '../../../../../modules/zorro.module';
import { CoachService } from '../../../../../services/coach.service';
import { UserService } from '../../../../../services/user.service';

const SAMPLE_WIREFRAME_REPORT: WireframeReportResponse = {
	wireframe_data: {
		player_title: 'Desert Animals Field Guide: Built to Survive the Heat',
		player_instructions: 'Read this desert animals field guide card. Then answer the questions.',
		assessment_objective:
			'Student can explain a cause-effect relationship, locate a specific animal detail, and identify a vocabulary definition by reading a desert animals field guide.',
	},
	student_results: {
		questions: [
			{
				lang_function: 'Identify cause and effect',
				assessment_objective:
					'Identify why fennec foxes have large ears by reading the cause-effect explanation in the text.',
				text: 'Why do fennec foxes have very large ears?',
				student_answer:
					"Student selected: 'To hear insects moving under the sand.' — the text does not mention hunting; it says body heat escapes through the ears to keep the fox cool.",
				correct_answer: 'To let body heat escape and stay cool.',
				correct: false,
				question_type: 'standard',
				feedback:
					'Look for the exact cause-and-effect clue in the text: the ears help the fox release heat, not find prey.',
			},
			{
				lang_function: 'Locate key details',
				assessment_objective:
					'Identify which animal can survive without drinking water by finding explicit details in the text.',
				text: 'Which desert animal can go a long time without drinking water?',
				student_answer: 'Kangaroo rat',
				correct_answer: 'Kangaroo rat',
				correct: true,
				question_type: 'standard',
			},
			{
				lang_function: 'Sequence events',
				assessment_objective: 'Put the events in the story structure order from beginning to end.',
				text: 'Order these plot elements from first to last.',
				student_answer: 'The beginning,Rising action,The climax,The resolution',
				correct_answer: 'The beginning,Rising action,The climax,The resolution',
				correct: null,
				question_type: 'sequence',
			},
			{
				lang_function: 'Sequence events',
				assessment_objective: 'Place the narrative structure labels in the correct order.',
				text: 'Order these story stages from first to last.',
				student_answer: 'The beginning,The climax,Rising action,The resolution',
				correct_answer: 'The beginning,Rising action,The climax,The resolution',
				correct: null,
				question_type: 'sequence',
				feedback: 'Great start. Swap the middle two steps so rising action comes before the climax.',
			},
		],
		summary: {
			firstAttempt: {
				count: 2,
				points: 1,
			},
			secondAttempt: {
				count: 0,
				points: 0.5,
			},
			incorrect: {
				count: 2,
				points: 0,
			},
		},
		score: '2 out of 4',
	},
};

@Component({
	selector: 'app-wireframe-report',
	standalone: true,
	imports: [AngularModule, ZorroModule],
	templateUrl: './wireframe-report.html',
	styleUrl: './wireframe-report.scss',
})
export class WireframeReport {
	takeNumber = input<number | undefined>();
	contentText = input<ContentText | undefined>();
	studentUuid = input<string | undefined>();

	// Set to true to force rendering local sample data.
	useSampleReport = false;

	// Keep mock data available for local development.
	readonly sampleReport = SAMPLE_WIREFRAME_REPORT;

	loadingWireframe = signal(false);
	report = signal<WireframeReportResponse | undefined>(undefined);
	questions = computed(() => this.report()?.student_results.questions || []);

	constructor(
		private coachService: CoachService,
		private userService: UserService
	) {
		effect(async () => {
			if (this.useSampleReport) {
				this.report.set(this.sampleReport);
				return;
			}

			const take = this.takeNumber();
			const content = this.contentText();
			const uuid = this.studentUuid();

			if (take === undefined || !content || !uuid) {
				this.report.set(undefined);
				return;
			}

			try {
				this.loadingWireframe.set(true);
				const cognitoUuid = await this.userService.convertUuidToCognitoUuid$(uuid);
				const wireframeData = await this.coachService.getWireframeReport(take, content, cognitoUuid);
				this.report.set(wireframeData);
			} catch (e: unknown) {
				console.error('Error loading wireframe report:', e);
				this.report.set(undefined);
			} finally {
				this.loadingWireframe.set(false);
			}
		});
	}

	public isSequenceQuestion(question: WireframeQuestion): boolean {
		return question.question_type === 'sequence';
	}

	public parseSequence(value: string): string[] {
		return value
			.split(',')
			.map(item => item.trim())
			.filter(item => item.length > 0);
	}

	public sequenceMatches(question: WireframeQuestion): boolean {
		if (!this.isSequenceQuestion(question)) {
			return false;
		}

		const studentSequence = this.parseSequence(question.student_answer);
		const correctSequence = this.parseSequence(question.correct_answer);

		if (studentSequence.length !== correctSequence.length) {
			return false;
		}

		return studentSequence.every(
			(item, index) => item.toLowerCase() === (correctSequence[index] || '').toLowerCase()
		);
	}

	public isSequenceItemCorrect(question: WireframeQuestion, index: number): boolean {
		if (!this.isSequenceQuestion(question)) {
			return false;
		}

		const studentSequence = this.parseSequence(question.student_answer);
		const correctSequence = this.parseSequence(question.correct_answer);

		return (studentSequence[index] || '').toLowerCase() === (correctSequence[index] || '').toLowerCase();
	}

	public getSequenceItemTooltip(question: WireframeQuestion, index: number): string {
		if (!this.isSequenceQuestion(question)) {
			return '';
		}

		if (this.isSequenceItemCorrect(question, index)) {
			return 'Correct position';
		}

		const correctSequence = this.parseSequence(question.correct_answer);
		return `Expected at this step: ${correctSequence[index] || 'N/A'}`;
	}

	public isQuestionIncorrect(question: WireframeQuestion): boolean {
		if (this.isSequenceQuestion(question)) {
			return !this.sequenceMatches(question);
		}

		return question.correct === false;
	}

	public getQuestionStatus(question: WireframeQuestion): string {
		if (this.isSequenceQuestion(question)) {
			return this.sequenceMatches(question) ? 'Correct' : 'Incorrect';
		}

		return question.correct ? 'Correct' : 'Incorrect';
	}
}
